import { Alert, Card, Descriptions, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import PortalCustodyRestrictedAlert from '@/pages/education/components/PortalCustodyRestrictedAlert';
import { isPortalCustodyRestricted } from '@/pages/education/components/portalCustody';

type Child = {
  id: string;
  admission_no: string;
  first_name: string;
  last_name?: string | null;
  status: string;
  relationship_name?: string | null;
  campus_name?: string | null;
  enrollment?: { grade_name?: string; section_name?: string; session_name?: string; status?: string } | null;
  attendance?: { total_marks: number; by_status: Record<string, number>; window_days: number };
  fees?: { outstanding_amount: number; open_demands: number };
};

type Home = {
  guardian: { first_name: string; last_name?: string | null } | null;
  children: Child[];
  message?: string;
};

export default function ParentPortalPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['education-portal-parent'],
    queryFn: async () => (await api.get<Home>('/education/portal/parent')).data,
    retry: (failureCount, error) => !isPortalCustodyRestricted(error) && failureCount < 2,
  });

  const home = query.data;

  // FO-B13: custody-restricted guardian. Alert only; never render children or guardian details.
  if (query.isError && isPortalCustodyRestricted(query.error)) {
    return (
      <EducationPageChrome title={t('parentPortal')} description={t('parentPortalIntro')}>
        <PortalCustodyRestrictedAlert />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome title={t('parentPortal')} description={t('parentPortalIntro')}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('portalGrantedOnlyHint')} />
        {home?.message ? <Alert type="warning" showIcon message={home.message} /> : null}
        {home?.guardian ? (
          <Card size="small">
            <Descriptions size="small" column={2}>
              <Descriptions.Item label={t('guardian')}>
                {[home.guardian.first_name, home.guardian.last_name].filter(Boolean).join(' ')}
              </Descriptions.Item>
              <Descriptions.Item label={t('children')}>{home.children?.length ?? 0}</Descriptions.Item>
            </Descriptions>
          </Card>
        ) : null}
        <Card size="small" loading={query.isLoading}>
          <Table
            rowKey="id"
            size="small"
            dataSource={home?.children ?? []}
            pagination={false}
            onRow={(row) => ({
              onClick: () => navigate(`/education/portal/parent/children/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('admissionNo'), dataIndex: 'admission_no', width: 120 },
              {
                title: t('student'),
                render: (_: unknown, row: Child) =>
                  [row.first_name, row.last_name].filter(Boolean).join(' '),
              },
              {
                title: t('grade'),
                render: (_: unknown, row: Child) =>
                  [row.enrollment?.grade_name, row.enrollment?.section_name].filter(Boolean).join(' / ') ||
                  '—',
              },
              {
                title: t('attendance'),
                render: (_: unknown, row: Child) =>
                  row.attendance
                    ? t('portalAttendanceSummary', {
                        total: row.attendance.total_marks,
                        days: row.attendance.window_days,
                      })
                    : '—',
              },
              {
                title: t('feesOutstanding'),
                render: (_: unknown, row: Child) => row.fees?.outstanding_amount ?? 0,
              },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 100,
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
